import { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useSubmitEnrollmentMutation } from "../app/api/enrollmentApi";
import Seo from "../components/Seo";

import { ArrowLeft, ArrowRight, Check } from "lucide-react";
type Track = "junior" | "senior" | "";
type Interest = string;

const juniorInterests = [
  "Game development",
  "Web design",
  "Animations & storytelling",
  "Robotics & circuits",
  "AI & technology",
  "Not sure yet",
];

const seniorInterests = [
  "Full-stack web development",
  "Mobile app development",
  "Artificial intelligence",
  "IoT & hardware",
  "Game development",
  "Cybersecurity",
  "Not sure yet",
];

interface ChildForm {
  childName: string;
  childAge: string;
  interests: Interest[];
}

interface FormData {
  parentName: string;
  email: string;
  phone: string;
  children: ChildForm[];
  message: string;
}

type ChildErrors = {
  childName?: string;
  childAge?: string;
};

type FormErrors = {
  parentName?: string;
  email?: string;
  phone?: string;
  children?: ChildErrors[];
};

const emptyChild = (): ChildForm => ({
  childName: "",
  childAge: "",
  interests: [],
});

const initialForm: FormData = {
  parentName: "",
  email: "",
  phone: "",
  children: [emptyChild()],
  message: "",
};

function getTrack(age: string): Track {
  if (age === "") return "";
  const n = parseInt(age, 10);
  if (n >= 6 && n <= 12) return "junior";
  if (n >= 13 && n <= 18) return "senior";
  return "";
}

function getInterestOptions(track: Track) {
  return track === "junior"
    ? juniorInterests
    : track === "senior"
      ? seniorInterests
      : [];
}

export default function EnrollPage() {
  const navigate = useNavigate();
  const [submitEnrollment, { isLoading }] = useSubmitEnrollmentMutation();

  const [form, setForm] = useState<FormData>(initialForm);
  const [step, setStep] = useState(1);
  const [submitError, setSubmitError] = useState("");
  const [errors, setErrors] = useState<FormErrors>({});

  const firstChildName = form.children[0]?.childName?.split(" ")[0] || "them";

  const summaryTracks = useMemo(
    () =>
      form.children.map((child) => ({
        ...child,
        track: getTrack(child.childAge),
      })),
    [form.children],
  );

  function set<K extends keyof FormData>(key: K, value: FormData[K]) {
    setForm((f) => ({ ...f, [key]: value }));
    setErrors((e) => ({ ...e, [key]: undefined }));
    setSubmitError("");
  }

  function updateChild(index: number, patch: Partial<ChildForm>) {
    setForm((prev) => {
      const nextChildren = [...prev.children];
      const current = nextChildren[index];

      const nextChild = { ...current, ...patch };

      if (patch.childAge !== undefined) {
        nextChild.interests = [];
      }

      nextChildren[index] = nextChild;
      return { ...prev, children: nextChildren };
    });

    setErrors((prev) => {
      const next = { ...prev };
      if (next.children?.[index]) {
        next.children[index] = {
          ...next.children[index],
          ...(patch.childName !== undefined ? { childName: undefined } : {}),
          ...(patch.childAge !== undefined ? { childAge: undefined } : {}),
        };
      }
      return next;
    });

    setSubmitError("");
  }

  function toggleChildInterest(index: number, interest: string) {
    setForm((prev) => {
      const nextChildren = [...prev.children];
      const child = nextChildren[index];

      nextChildren[index] = {
        ...child,
        interests: child.interests.includes(interest)
          ? child.interests.filter((i) => i !== interest)
          : [...child.interests, interest],
      };

      return { ...prev, children: nextChildren };
    });

    setSubmitError("");
  }

  function addChild() {
    setForm((prev) => ({
      ...prev,
      children: [...prev.children, emptyChild()],
    }));
  }

  function removeChild(index: number) {
    setForm((prev) => {
      if (prev.children.length === 1) return prev;
      return {
        ...prev,
        children: prev.children.filter((_, i) => i !== index),
      };
    });

    setErrors((prev) => ({
      ...prev,
      children: prev.children?.filter((_, i) => i !== index),
    }));
  }

  function validateStep1() {
    const e: FormErrors = {};

    if (!form.parentName.trim()) e.parentName = "Please enter your name";
    if (!form.email.trim() || !/\S+@\S+\.\S+/.test(form.email)) {
      e.email = "Please enter a valid email";
    }
    if (!form.phone.trim()) e.phone = "Please enter a phone number";

    setErrors(e);
    return Object.keys(e).length === 0;
  }

  function validateStep2() {
    const childErrors: ChildErrors[] = form.children.map((child) => {
      const childError: ChildErrors = {};

      if (!child.childName.trim()) {
        childError.childName = "Please enter the child's name";
      }

      const age = parseInt(child.childAge, 10);
      if (!child.childAge || age < 6 || age > 18 || Number.isNaN(age)) {
        childError.childAge = "Age must be between 6 and 18";
      }

      return childError;
    });

    const hasErrors = childErrors.some(
      (childError) => Object.keys(childError).length > 0,
    );

    const e: FormErrors = {};
    if (hasErrors) e.children = childErrors;

    setErrors((prev) => ({
      ...prev,
      children: hasErrors ? childErrors : undefined,
    }));

    return !hasErrors;
  }

  function next() {
    if (step === 1 && validateStep1()) setStep(2);
    if (step === 2 && validateStep2()) setStep(3);
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSubmitError("");

    if (!validateStep2()) {
      setStep(2);
      return;
    }

    try {
      await submitEnrollment({
        parentName: form.parentName.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        children: form.children.map((child) => ({
          childName: child.childName.trim(),
          childAge: Number(child.childAge),
          interests: child.interests,
        })),
        message: form.message.trim(),
      }).unwrap();

      navigate("/thank-you", {
        state: {
          childName:
            form.children.length === 1
              ? form.children[0].childName
              : `${form.children.length} children`,
        },
      });
    } catch (error: any) {
      setSubmitError(
        error?.data?.message ||
          error?.message ||
          "Something went wrong while submitting. Please try again.",
      );
    }
  }

  const progress = (step / 3) * 100;

  return (
    <div className="min-h-screen bg-[#f5f4ef] text-[#16213e]">
      <Seo title="Enroll your child" description="Enroll your child (ages 6–18) in Future Smart Lab's technology, AI, STEM, leadership and entrepreneurship programme. Now enrolling in Abuja." path="/enroll" />
      <nav className="border-b border-[#cdd4e2] bg-[#f5f4ef]">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-6 py-5 md:px-10">
          <Link to="/" className="flex items-center gap-3">
            <img src="/images/logo.png" alt="" className="h-11 w-11 rounded-2xl" />
            <span className="text-lg font-extrabold tracking-tight text-[#16213e]">
              Future Smart Lab
            </span>
          </Link>

          <Link
            to="/"
            className="rounded-full border border-[#cdd4e2] bg-white px-5 py-2.5 text-sm font-medium text-[#5a6278] transition hover:bg-[#f7f8fb]"
          >
           {" "}<ArrowLeft className="inline-block h-[1em] w-[1em] align-[-0.125em]" aria-hidden />{" "}Back
          </Link>
        </div>
      </nav>

      <div className="h-1 bg-[#dde2ec]">
        <div
          className="h-full rounded-r-full bg-[#1b2d5e] transition-all duration-500"
          style={{ width: `${progress}%` }}
        />
      </div>

      <div className="mx-auto flex min-h-[calc(100vh-88px)] max-w-6xl items-center justify-center px-5 py-10">
        <div className="grid w-full gap-6 lg:grid-cols-[0.9fr_1.1fr]">
          <div className="hidden overflow-hidden rounded-[34px] bg-[#1b2d5e] p-8 text-white shadow-[0_12px_40px_rgba(27,45,94,0.08)] lg:block">
            <div className="flex h-full flex-col justify-between">
              <div>
                <p className="rounded-full bg-white/20 px-4 py-2 text-xs font-medium inline-flex">
                  Future-ready learning
                </p>
                <h2 className="mt-8 text-[3.1rem] font-semibold leading-[0.95] tracking-[-0.04em]">
                  Turn learning into momentum.
                </h2>
                <p className="mt-5 max-w-sm text-base leading-7 text-white/80">
                  Register your child and we’ll guide you to the right track,
                  the right schedule, and the right next step.
                </p>
              </div>

              <div className="space-y-4">
                {[
                  "Personalized pathway",
                  "Real projects",
                  "Age-based learning tracks",
                ].map((item) => (
                  <div
                    key={item}
                    className="rounded-[22px] bg-white/14 px-5 py-4 text-sm font-medium backdrop-blur-sm"
                  >
                    {item}
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="overflow-hidden rounded-[34px] border border-[#dde2ec] bg-white shadow-[0_12px_40px_rgba(27,45,94,0.06)]">
            <div className="px-6 py-7 md:px-10 md:py-9">
              <div className="mb-10 flex items-center gap-3">
                {[1, 2, 3].map((s) => (
                  <div key={s} className="flex items-center gap-3">
                    <div
                      className={`flex h-10 w-10 items-center justify-center rounded-full border text-sm font-semibold transition-all duration-300 ${
                        s < step
                          ? "border-[#1b2d5e] bg-[#1b2d5e] text-white"
                          : s === step
                            ? "border-[#1b2d5e] bg-[#f7f8fb] text-[#1b2d5e]"
                            : "border-[#dde2ec] bg-white text-[#a6adbf]"
                      }`}
                    >
                      {s < step ? <Check className="h-4 w-4" aria-hidden /> : s}
                    </div>
                    {s < 3 && (
                      <div
                        className={`h-[2px] w-10 rounded-full ${
                          s < step ? "bg-[#1b2d5e]" : "bg-[#e5e8ef]"
                        }`}
                      />
                    )}
                  </div>
                ))}
                <span className="ml-2 text-sm font-medium text-[#8189a0]">
                  Step {step} of 3
                </span>
              </div>

              <form onSubmit={handleSubmit}>
                {step === 1 && (
                  <div className="space-y-6">
                    <div>
                      <h1 className="text-[2.8rem] font-semibold leading-[0.95] tracking-[-0.04em] text-[#16213e]">
                        Let&apos;s start with you
                      </h1>
                      <p className="mt-3 max-w-md text-base leading-7 text-[#5f6780]">
                        We&apos;ll use these details to reach out with program
                        information and next steps.
                      </p>
                    </div>

                    <Field label="Your full name" error={errors.parentName}>
                      <input
                        type="text"
                        placeholder="e.g. Adaeze Okonkwo"
                        value={form.parentName}
                        onChange={(e) => set("parentName", e.target.value)}
                        className={inputClass(!!errors.parentName)}
                      />
                    </Field>

                    <Field label="Email address" error={errors.email}>
                      <input
                        type="email"
                        placeholder="you@example.com"
                        value={form.email}
                        onChange={(e) => set("email", e.target.value)}
                        className={inputClass(!!errors.email)}
                      />
                    </Field>

                    <Field label="WhatsApp / Phone number" error={errors.phone}>
                      <input
                        type="tel"
                        placeholder="+234 800 000 0000"
                        value={form.phone}
                        onChange={(e) => set("phone", e.target.value)}
                        className={inputClass(!!errors.phone)}
                      />
                    </Field>

                    <NavButtons onNext={next} step={step} />
                  </div>
                )}

                {step === 2 && (
                  <div className="space-y-6">
                    <div>
                      <h1 className="text-[2.8rem] font-semibold leading-[0.95] tracking-[-0.04em] text-[#16213e]">
                        Tell us about your children
                      </h1>
                      <p className="mt-3 max-w-md text-base leading-7 text-[#5f6780]">
                        Add one or more children and we&apos;ll automatically
                        match each child to the right learning track.
                      </p>
                    </div>

                    <div className="space-y-5">
                      {form.children.map((child, index) => {
                        const track = getTrack(child.childAge);

                        return (
                          <div
                            key={index}
                            className="rounded-[24px] border border-[#dde2ec] bg-[#f9fafc] p-5"
                          >
                            <div className="mb-4 flex items-center justify-between">
                              <h3 className="text-sm font-semibold text-[#3b4560]">
                                Child {index + 1}
                              </h3>

                              {form.children.length > 1 && (
                                <button
                                  type="button"
                                  onClick={() => removeChild(index)}
                                  className="text-sm font-medium text-[#c06a60] hover:underline"
                                >
                                  Remove
                                </button>
                              )}
                            </div>

                            <div className="space-y-4">
                              <Field
                                label="Child's full name"
                                error={errors.children?.[index]?.childName}
                              >
                                <input
                                  type="text"
                                  placeholder="e.g. Chidi Okonkwo"
                                  value={child.childName}
                                  onChange={(e) =>
                                    updateChild(index, {
                                      childName: e.target.value,
                                    })
                                  }
                                  className={inputClass(
                                    !!errors.children?.[index]?.childName,
                                  )}
                                />
                              </Field>

                              <Field
                                label="Child's age"
                                error={errors.children?.[index]?.childAge}
                                hint="We accept ages 6 through 18"
                              >
                                <input
                                  type="number"
                                  placeholder="e.g. 10"
                                  min={6}
                                  max={18}
                                  value={child.childAge}
                                  onChange={(e) =>
                                    updateChild(index, {
                                      childAge: e.target.value,
                                    })
                                  }
                                  className={inputClass(
                                    !!errors.children?.[index]?.childAge,
                                  )}
                                />
                              </Field>

                              {track && (
                                <div
                                  className={`rounded-[20px] border p-4 ${
                                    track === "junior"
                                      ? "border-[#cdd4e2] bg-[#f7f8fb]"
                                      : "border-[#efc1b9] bg-[#fff4f1]"
                                  }`}
                                >
                                  <p
                                    className={`text-xs font-semibold uppercase tracking-[0.2em] ${
                                      track === "junior"
                                        ? "text-[#1b2d5e]"
                                        : "text-[#d98575]"
                                    }`}
                                  >
                                    {track === "junior"
                                      ? "Junior Builders Track"
                                      : "Senior Builders Track"}
                                  </p>
                                  <p className="mt-2 text-sm leading-6 text-[#5a6278]">
                                    {track === "junior"
                                      ? "Ages 6–12 · Scratch, HTML, CSS, JavaScript, APIs, and AI fundamentals"
                                      : "Ages 13–18 · Full-stack MERN, React Native, IoT, and AI applications"}
                                  </p>
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>

                    <button
                      type="button"
                      onClick={addChild}
                      className="rounded-full border border-[#cdd4e2] bg-white px-5 py-3 text-sm font-semibold text-[#5b54c8] transition hover:bg-[#f7f8fb]"
                    >
                      + Add another child
                    </button>

                    <NavButtons
                      onNext={next}
                      onBack={() => setStep(1)}
                      step={step}
                    />
                  </div>
                )}

                {step === 3 && (
                  <div className="space-y-6">
                    <div>
                      <h1 className="text-[2.8rem] font-semibold leading-[0.95] tracking-[-0.04em] text-[#16213e]">
                        What excites {firstChildName} and the others?
                      </h1>
                      <p className="mt-3 max-w-md text-base leading-7 text-[#5f6780]">
                        Select interests for each child. This helps us
                        personalise their experience.
                      </p>
                    </div>

                    <div className="space-y-5">
                      {form.children.map((child, index) => {
                        const track = getTrack(child.childAge);
                        const options = getInterestOptions(track);

                        return (
                          <div
                            key={index}
                            className="rounded-[24px] border border-[#dde2ec] bg-[#f9fafc] p-5"
                          >
                            <p className="mb-4 text-sm font-semibold text-[#3b4560]">
                              {child.childName || `Child ${index + 1}`}
                            </p>

                            {options.length > 0 && (
                              <div className="grid grid-cols-2 gap-3">
                                {options.map((interest) => {
                                  const selected =
                                    child.interests.includes(interest);

                                  return (
                                    <button
                                      key={interest}
                                      type="button"
                                      onClick={() =>
                                        toggleChildInterest(index, interest)
                                      }
                                      className={`rounded-[20px] border px-4 py-4 text-left text-sm font-medium transition ${
                                        selected
                                          ? "border-[#1b2d5e] bg-[#f7f8fb] text-[#5b54c8]"
                                          : "border-[#dde2ec] bg-[#f9fafc] text-[#5a6278] hover:bg-[#f7f8fb]"
                                      }`}
                                    >
                                      {selected && (
                                        <span className="mr-1"><Check className="h-4 w-4" aria-hidden /></span>
                                      )}
                                      {interest}
                                    </button>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>

                    <Field
                      label="Anything else you'd like us to know?"
                      hint="Optional"
                    >
                      <textarea
                        rows={3}
                        placeholder="Any questions, special needs, or things we should know about your child or children..."
                        value={form.message}
                        onChange={(e) => set("message", e.target.value)}
                        className={`${inputClass(false)} resize-none`}
                      />
                    </Field>

                    {submitError && (
                      <div className="rounded-[20px] border border-[#efc4c1] bg-[#fff3f1] px-4 py-3 text-sm text-[#c06a60]">
                        {submitError}
                      </div>
                    )}

                    <div className="rounded-[26px] border border-[#dde2ec] bg-[#f9fafc] p-5 text-sm">
                      <p className="mb-4 text-xs font-semibold uppercase tracking-[0.2em] text-[#8189a0]">
                        Summary
                      </p>

                      <div className="space-y-2 mb-5">
                        <Row label="Parent" value={form.parentName} />
                        <Row label="Email" value={form.email} />
                        <Row label="Phone" value={form.phone} />
                        <Row
                          label="Children"
                          value={`${form.children.length} ${
                            form.children.length === 1 ? "child" : "children"
                          }`}
                        />
                      </div>

                      <div className="space-y-3">
                        {summaryTracks.map((child, index) => (
                          <div
                            key={index}
                            className="rounded-[18px] border border-[#e5e8ef] bg-white px-4 py-3"
                          >
                            <div className="flex justify-between gap-4">
                              <span className="text-[#8189a0]">
                                {child.childName || `Child ${index + 1}`}
                              </span>
                              <span className="text-right font-medium text-[#3b4560]">
                                {child.childAge} years ·{" "}
                                {child.track === "junior"
                                  ? "Junior Builders"
                                  : child.track === "senior"
                                    ? "Senior Builders"
                                    : "No track"}
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div className="flex gap-3">
                      <button
                        type="button"
                        onClick={() => setStep(2)}
                        className="flex-1 rounded-full border border-[#dde2ec] bg-white py-4 text-sm font-medium text-[#5a6278] transition hover:bg-[#f7f8fb]"
                      >
                       {" "}<ArrowLeft className="inline-block h-[1em] w-[1em] align-[-0.125em]" aria-hidden />{" "}Back
                      </button>

                      <button
                        type="submit"
                        disabled={isLoading}
                        className="flex-[2] rounded-full bg-gradient-to-r from-brand-600 to-brand-400 py-4 text-sm font-semibold text-white shadow-glow transition hover:-translate-y-0.5 hover:shadow-glow-lg disabled:cursor-not-allowed disabled:opacity-60"
                      >
                        {isLoading ? (
                          <span className="flex items-center justify-center gap-2">
                            <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/40 border-t-white" />
                            Submitting...
                          </span>
                        ) : (
                          <>Submit registration <ArrowRight className="inline-block h-[1em] w-[1em] align-[-0.125em]" aria-hidden /></>
                        )}
                      </button>
                    </div>
                  </div>
                )}
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({
  label,
  error,
  hint,
  children,
}: {
  label: string;
  error?: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-2">
      <label className="block text-xs font-semibold uppercase tracking-[0.2em] text-[#8189a0]">
        {label}
      </label>
      {children}
      {hint && !error && <p className="text-xs text-[#a6adbf]">{hint}</p>}
      {error && <p className="text-xs text-[#c06a60]">{error}</p>}
    </div>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between gap-4">
      <span className="text-[#8189a0]">{label}</span>
      <span className="text-right font-medium text-[#3b4560]">{value}</span>
    </div>
  );
}

function NavButtons({
  onNext,
  onBack,
}: {
  onNext: () => void;
  onBack?: () => void;
  step: number;
}) {
  return (
    <div className="flex gap-3 pt-2">
      {onBack && (
        <button
          type="button"
          onClick={onBack}
          className="flex-1 rounded-full border border-[#dde2ec] bg-white py-4 text-sm font-medium text-[#5a6278] transition hover:bg-[#f7f8fb]"
        >
         {" "}<ArrowLeft className="inline-block h-[1em] w-[1em] align-[-0.125em]" aria-hidden />{" "}Back
        </button>
      )}
      <button
        type="button"
        onClick={onNext}
        className="flex-[2] rounded-full bg-gradient-to-r from-brand-600 to-brand-400 py-4 text-sm font-semibold text-white shadow-glow transition hover:-translate-y-0.5 hover:shadow-glow-lg"
      >
        Continue{" "}<ArrowRight className="inline-block h-[1em] w-[1em] align-[-0.125em]" aria-hidden />
      </button>
    </div>
  );
}

function inputClass(hasError: boolean) {
  return `w-full rounded-[18px] border bg-[#f9fafc] px-4 py-4 text-sm text-[#16213e] placeholder:text-[#a6adbf] outline-none transition ${
    hasError
      ? "border-[#efc4c1] focus:border-[#d98575]"
      : "border-[#dde2ec] focus:border-[#1b2d5e]"
  }`;
}
